/**
 * Lazy Descriptor Library Loader
 * 
 * Implements lazy loading and browser caching for the descriptor library.
 * Reduces initial bundle size by loading descriptors on-demand.
 * 
 * Requirements: 4.1, 4.2
 */

// Browser cache key for descriptor library
const CACHE_KEY = 'taiste-descriptor-library-v2.2';
const CACHE_TIMESTAMP_KEY = 'taiste-descriptor-library-timestamp';
const CACHE_TTL = 60 * 60 * 1000; // 1 hour in milliseconds

interface CachedDescriptors {
  version: string;
  data: any;
}

/**
 * Check if browser cache is valid
 */
function isBrowserCacheValid(): boolean {
  try {
    const timestamp = localStorage.getItem(CACHE_TIMESTAMP_KEY);
    if (!timestamp) return false;
    
    const age = Date.now() - parseInt(timestamp, 10);
    return age < CACHE_TTL;
  } catch {
    return false;
  }
}

/**
 * Get descriptors from browser cache
 */
function getFromBrowserCache(): CachedDescriptors | null {
  try {
    const cached = localStorage.getItem(CACHE_KEY);
    if (!cached) return null;
    
    return JSON.parse(cached);
  } catch {
    return null;
  }
}

/**
 * Save descriptors to browser cache
 */
function saveToBrowserCache(data: CachedDescriptors): void {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify(data));
    localStorage.setItem(CACHE_TIMESTAMP_KEY, Date.now().toString());
  } catch (err) {
    console.warn('Failed to cache descriptors in localStorage:', err);
  }
}

/**
 * Clear descriptor cache
 */
export function clearDescriptorBrowserCache(): void {
  try {
    localStorage.removeItem(CACHE_KEY);
    localStorage.removeItem(CACHE_TIMESTAMP_KEY);
  } catch {
    // Ignore errors
  }
}

/**
 * Lazy load descriptor library with browser caching
 * 
 * This function:
 * 1. Checks browser localStorage cache first
 * 2. If cache is valid, returns cached data immediately
 * 3. If cache is invalid/missing, dynamically imports the descriptor library
 * 4. Caches the result in localStorage for future use
 * 
 * @returns Promise resolving to the descriptor library module
 */
export async function loadDescriptorLibrary(): Promise<typeof import('../../data/descriptor-library')> {
  // Check browser cache first
  if (isBrowserCacheValid()) {
    const cached = getFromBrowserCache();
    if (cached && cached.version === '2.2') {
      // Return cached data wrapped in the expected module structure
      return cached.data as typeof import('../../data/descriptor-library');
    }
  }
  
  // Dynamically import the descriptor library (code splitting)
  const descriptorModule = await import('../../data/descriptor-library');
  
  // Cache in browser storage
  saveToBrowserCache({
    version: descriptorModule.DESCRIPTOR_LIBRARY_VERSION,
    data: descriptorModule,
  });
  
  return descriptorModule;
}

/**
 * Preload descriptor library in the background
 * Call this on app initialization to warm up the cache
 */
export function preloadDescriptorLibrary(): void {
  // Use requestIdleCallback if available, otherwise setTimeout
  if ('requestIdleCallback' in window) {
    requestIdleCallback(() => {
      loadDescriptorLibrary().catch(err => {
        console.warn('Failed to preload descriptor library:', err);
      });
    });
  } else {
    setTimeout(() => {
      loadDescriptorLibrary().catch(err => {
        console.warn('Failed to preload descriptor library:', err);
      });
    }, 100);
  }
}
